﻿import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../app/api";
import { applyUiFrameworkMode } from "../app/ui-framework";
import { loadUiSettings, type OwnerThemeSettings } from "../app/ui-settings";

const ownerLoginBackgroundUrl = new URL("../assets/Hintergrundgrafik.png", import.meta.url).toString();
const ownerLoginVisualUrl = new URL("../assets/Bild_Owner_login.png", import.meta.url).toString();

type OwnerBranding = {
  firmenname?: string;
  email?: string;
  logoDataUrl?: string;
  uiFramework?: "classic" | "mockup_v2";
  theme: OwnerThemeSettings;
};

const loadOwnerBranding = (): OwnerBranding => {
  const ui = loadUiSettings();
  return {
    firmenname: ui.management.firmenname,
    email: ui.management.email,
    logoDataUrl: ui.ownerLogoDataUrl,
    uiFramework: ui.management.uiFramework,
    theme: ui.ownerTheme
  };
};

const ownerThemeVars = (theme: OwnerThemeSettings): CSSProperties =>
  ({
    "--bg": theme.bg,
    "--surface": theme.surface,
    "--line": theme.line,
    "--text": theme.text,
    "--primary": theme.primary,
    "--owner-gradient-from": theme.gradientFrom,
    "--owner-gradient-to": theme.gradientTo,
    "--owner-gradient-enabled": theme.gradientEnabled ? "1" : "0",
    "--owner-gradient-strength": theme.gradientEnabled ? "100%" : "0%",
    "--owner-login-bg-image-enabled": theme.ownerLoginBackgroundImageEnabled ? "1" : "0",
    "--owner-login-bg-image": `url("${ownerLoginBackgroundUrl}")`,
    "--owner-login-visual-image": `url("${ownerLoginVisualUrl}")`
  }) as CSSProperties;

export const OwnerVotingLoginPage = () => {
  const navigate = useNavigate();
  const [beschlussId, setBeschlussId] = useState("");
  const [zugangspasswort, setZugangspasswort] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const localBranding = useMemo(() => loadOwnerBranding(), []);
  const brandingQuery = useQuery({
    queryKey: ["ui-settings-public"],
    queryFn: () => api.uiBranding(),
    retry: 1,
    staleTime: 60_000
  });
  const branding = useMemo(() => {
    const ui = brandingQuery.data;
    if (!ui) return localBranding;
    return {
      firmenname: ui.management.firmenname,
      email: ui.management.email,
      logoDataUrl: ui.ownerLogoDataUrl,
      uiFramework: ui.management.uiFramework,
      theme: ui.ownerTheme
    };
  }, [brandingQuery.data, localBranding]);
  const companyName = String(branding.firmenname || "ObjektVote").trim();
  useEffect(() => {
    applyUiFrameworkMode(branding.uiFramework === "mockup_v2" ? "mockup_v2" : "classic");
  }, [branding.uiFramework]);
  useEffect(() => {
    document.title = "ObjektVote | Login";
  }, []);

  const loginMutation = useMutation({
    mutationFn: () => api.ownerVotingLogin({ beschluss_id: beschlussId.trim(), zugangspasswort: zugangspasswort.trim() }),
    onSuccess: (data) => {
      setError("");
      navigate(`${data.redirect_path}?mode=login`);
    },
    onError: (e) => {
      const raw = e instanceof Error ? e.message : "Login fehlgeschlagen";
      if (raw.includes("401")) {
        setError("Zugangspasswort ungültig.");
        return;
      }
      if (raw.includes("404")) {
        setError("Beschluss wurde nicht gefunden.");
        return;
      }
      setError("Anmeldung fehlgeschlagen. Bitte Eingaben prüfen und erneut versuchen.");
    }
  });

  return (
    <main
      className={`owner-auth-shell owner-auth-shell-centered owner-login-shell${
        branding.theme.ownerLoginBackgroundImageEnabled ? " owner-login-shell--bg-on" : ""
      }`}
      style={ownerThemeVars(branding.theme)}
    >
      <section className="panel owner-login-card">
        <div className="owner-auth-head owner-login-head">
          <div>
            <div className="owner-auth-kicker owner-login-kicker">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="owner-login-kicker-icon">
                <path d="M12 3.5 5 6v5.4c0 4.3 2.7 8.2 7 9.8 4.3-1.6 7-5.5 7-9.8V6l-7-2.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="m9.3 12 1.8 1.9 3.6-3.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Digitale Abstimmung</span>
            </div>
            <h1 className="owner-auth-title owner-login-title">ObjektVote</h1>
            <p className="owner-auth-subtitle owner-login-subtitle">Ihre Stimme sicher online abgeben.</p>
          </div>
          {branding.logoDataUrl ? <img src={branding.logoDataUrl} alt="Firmenlogo" className="owner-auth-logo owner-auth-logo-right owner-login-logo" /> : null}
        </div>

        <hr className="owner-auth-divider owner-login-divider" />

        <div className="owner-login-main-grid">
          <div className="owner-login-form-col">
            <h2 className="owner-login-section-title">Anmeldung</h2>

            <div className="form-field owner-login-field">
              <label>Beschluss-ID</label>
              <div className="owner-login-input-wrap">
                <span className="owner-login-input-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="5" y="3.8" width="14" height="16.4" rx="2" stroke="currentColor" strokeWidth="1.7" />
                    <path d="M9 8.2h6M9 12h6M9 15.8h4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                </span>
                <input
                  className="table-field owner-login-input"
                  value={beschlussId}
                  onChange={(e) => setBeschlussId(e.target.value.slice(0, 10))}
                  placeholder="z.B. B-2026-001"
                  autoComplete="off"
                  maxLength={10}
                />
              </div>
            </div>

            <div className="form-field owner-login-field owner-auth-password-field">
              <label>Zugangspasswort</label>
              <div className="owner-login-input-wrap owner-login-password-wrap">
                <span className="owner-login-input-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="4.2" y="10.5" width="15.6" height="10.5" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8.4 10.5V7.8a3.6 3.6 0 1 1 7.2 0v2.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <input
                  className="table-field owner-login-input"
                  type={showPassword ? "text" : "password"}
                  value={zugangspasswort}
                  onChange={(e) => setZugangspasswort(e.target.value.replace(/\s+/g, "").slice(0, 6))}
                  placeholder="6-stelliges Passwort"
                  autoComplete="current-password"
                  maxLength={6}
                />
                <button
                  type="button"
                  className="owner-login-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                  title={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error ? <div className="error-text">{error}</div> : null}

            <div className="form-actions owner-login-actions">
              <button
                type="button"
                className="owner-login-submit"
                disabled={loginMutation.isPending || !beschlussId.trim() || !zugangspasswort.trim()}
                onClick={() => loginMutation.mutate()}
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="4.2" y="10.5" width="15.6" height="10.5" rx="2" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M8.4 10.5V7.8a3.6 3.6 0 1 1 7.2 0v2.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <span>{loginMutation.isPending ? "Anmeldung läuft..." : "Zur Abstimmung"}</span>
              </button>
            </div>
          </div>

          <div className="owner-login-visual-col" aria-hidden="true">
            <img src={ownerLoginVisualUrl} alt="" className="owner-login-visual-image" />
          </div>
        </div>

        <div className="owner-login-footer">
          <div className="owner-login-footer-block">
            <span className="owner-login-footer-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <rect x="3.6" y="5.2" width="16.8" height="13.6" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
                <path d="m5.6 7 6.4 5 6.4-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div className="owner-login-footer-copy">
              <strong>{companyName}</strong>
              {branding.email ? <a href={`mailto:${branding.email}`}>{branding.email}</a> : null}
            </div>
          </div>
          <div className="owner-login-footer-block">
            <span className="owner-login-footer-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 3.5 5 6v5.4c0 4.3 2.7 8.2 7 9.8 4.3-1.6 7-5.5 7-9.8V6l-7-2.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div className="owner-login-footer-copy">
              <strong>Sichere &amp; verschlüsselte Verbindung</strong>
              <span>Ihre Daten sind bei uns sicher.</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};




